import React from "react";

function TheLaw() {
  return (
    <div>
      <article className="prose lg:prose-base max-w-none">
        <h2 className="underline underline-offset-8">The Law</h2>
        <p>On the 16 th of December 2022, the Base Minerals Export Control (Lithium Bearing Ores and Unbeneficiated Lithium) Order, 2022 (S.I. 213 of 2022) was promulgated.</p>

        In brief S.I. 213 of 2022 provided as follows:
        <br />
        <p>
          1. The exportation of unbeneficiated lithium from Zimbabwe was
          prohibited except under the written permit of the Minister of Mines
          and Mining Development.
          <br />
          2. The permit could only be issued under the following circumstances:
          <br />
          i. for the purposes of exporting lithium samples outside Zimbabwe for
          assaying; or
          <br />
          ii. where a miner or lithium exporter provides satisfactory proof that
          exceptional circumstances exist that would justify the exportation of
          unbeneficiated lithium as well as proof that the unbeneficiated
          lithium intended to be exported has been valued in terms of Section
          12D of the Value Added Tax Act for the purposes of paying the export
          tax on unbeneficiated lithium.
          <br />
          3. The value of the unbeneficiated lithium would either be determined
          using either its market value on the date of exportation with
          reference to a reputable metals exchange or its value as reflected on
          any document required to be delivered in terms of the Customs and
          Excise Act [ Chapter 23:02] for its exportation under that act.
          <br />
          4. It reiterates the effect of section 5 of the Base Minerals Export
          Act which makes an order made in terms thereof effective
          notwithstanding the existence of any other inconsistent enactment,
          trade or custom.
          <br />
          5. Finally, it reiterates section 6 of the Base Minerals Export Act
          which makes failure to comply with an order or with the terms and
          conditions of a permit made in terms of that order. The prescribed
          penalty for the offence was a fine not exceeding level 9 or twice the
          value of the base minerals in respect of which the offence was
          committed or imprisonment for period not exceeding two years or both
          such fine and imprisonment.
        </p>
        <p>
          Shortly after the promulgation of S.I. 213 of 2022 came the Base
          Minerals Export Control (Unbeneficiated Base Minerals) Order, 2023
          (S.I. 5 of 2023) which repealed S.I. 213 of 2022 in its entirety.
        </p>
        <p>
          S.I. 5 of 2023 seems to be an attempt to deal more comprehensively
          with the regulation of the exportation of unbeneficiated base mineral
          ores. Like its predecessor S.I. 5 of 2023 prohibits the exportation
          outside of Zimbabwe of unbeneficiated base mineral ores except under
          the written permit of the Minister. It introduces an application
          procedure for the permit and widens the scope of who may apply for the
          permit to include “other interested persons.”
        </p>
        <p>
          It stipulates the following circumstances under which the application
          may be made:
        </p>
        1. Where the applicant produces compelling reasons to the minister
        showing that the unbeneficiated base mineral ore that he intends to
        export is not capable of being beneficiated to any extent within
        Zimbabwe
        <br />
        2. Where the applicant produces proof satisfactory to the Minister that
        <br />
        i. the unbeneficiated base mineral ore is being exported for assaying
        purposes.
        <br />
        ii. such assay cannot be satisfactorily be done in Zimbabwe; and
        <br />
        iii. the quantity sought be exported is necessary for the purpose of
        assaying.
        <br />
        <h2>How to Obtain a Permit </h2>
        <p>
          One assumes that it is easier to obtain a permit to export for assay
          as opposed to exporting for purely commercial reasons. This is because
          exporting for commercial reasons with little or no benefit to the
          Zimbabwe economy is exactly what the regulations are meant to curtail.
        </p>
        <p>
          There is no standard form to be used in the application which must be
          directed to the Minister of Mines and Mining Development. This can be
          done by means of a letter attaching all documents that the miner
          considers necessary for the success of the application. There are no
          guidelines on what the Minister will consider but it is likely that
          the extent to which the applicant company is engaged in corporate
          social responsibility activities that benefit the locals and the
          economy and an undertaking that the proceeds from the export will be
          channelled to Zimbabwe with only profits being repatriated in terms of
          the Exchange Control legislation. At the end of the day, the minister
          exercises his discretion, and his decision must be accompanied by
          reasons as required under administrative law.
        </p>
        <p>
          The regulations are couched in a manner that suggests that the
          application is determined “on the papers” but there is always room for
          clients to request to make oral representations. It is advisable to do
          so as this will present client with a chance to speed up the process
          and clear up any issues that may not be apparent from the application.
          There are no timelines, and the turnaround times will depend on how
          much the client pushes for a decision.
        </p>
        <p>
          After obtaining a waiver from the Minister an application for an
          export permit must then be submitted to the Mineral Marketing
          Corporation. A fee of USD 1000-00 must accompany the application.
          Local media has reported that only the Zimbabwe Defence Industries has
          been granted the permit. There is no public information on the number
          of permits issued so far.
        </p>
        <h2>Impact on Mining Business in Zimbabwe</h2>
        <p>
          The minerals and metal sector in Africa is heavily affected by export
          control measures of raw materials. Export control measures are amongst
          the tools which governments employ with the aim to encourage local
          processing and capture more of the value flowing from minerals
          extraction.
        </p>
        <p>
          By putting restraints on the export of raw materials, governments hope
          to divert these materials to the domestic market, thereby supporting
          local activities to process these materials and employment. However,
          export restrictions can also have costs for the countries employing
          them; in particular, they can lower the returns on raw materials
          production and entail efficiency costs for the economy overall.
        </p>
        <p>
          In 2017 the Organisation for Economic Co-Operation and Development
          published a report on Export Controls and Competitiveness in African
          Mining and Minerals Processing Industries. 1 The four case studies
          selected for the analysis in the report concerned the manganese sector
          in Gabon, the primary and secondary lead sector in South Africa, the
          copper sector in Zambia and the chromium sector in Zimbabwe.
        </p>
        <p>
          The report describes developments in the industries over a 20-year
          time span from 1992 through to 2013. It examines the revealed
          comparative advantages and disadvantages of mineral export controls
          and determined whether changes have occurred in the relative
          competitiveness of raw and related processed products and whether
          these could be attributed to export control measures. The focus of the
          analysis was on the effect of export controls on mineral processing at
          the initial stages of the value chains of these industries, which we
          suspect is also the primary focus of of S.I. 5 of 2023 given the
          nascence of beneficiation in general in the mining industry. The
          overall result of the analysis in the report finds that export control
          measures may have had an adverse effect on the mining industries in
          Africa and that the correspondent processing industries have not
          benefited but have rather been stunted as a result.
        </p>
        <p>
          The primary reason for this is that, on the one hand the respective
          export control measures had a limiting effect on the capacity of each
          subject country to keep up with global demand for its raw minerals,
          thus leaving opportunity for competitor countries without similar
          restrictions to fill the gap, as it were . On the other hand, the
          products of the subject country’s local downstream processing
          industries failed to compete on the global market with products from
          far more advanced countries.
        </p>
        <p>
          Another reason identified in the report for the negative effect of
          export controls which directly related to the chromium export ban in
          Zimbabwe was the lack of a local market to which producers of raw
          chromite could sell. It is reported that when the ban was introduced
          producers of chromite ore had difficulties finding local processors.
          Unable to sell abroad and locally, some mining operations closed down
          completely. The report states that this was not in the interest of the
          Zimbabwean economy, which depends on the mining sector for foreign
          exchange, and not a circumstance that the government appears to have
          thought of when it imposed the export ban – the strictest of all
          export control measures –hoping to attract investment in the country’s
          smelting capacity and further up the value chain.
        </p>
        <p>
          The lesson to be gleaned from the above is that a local market for raw
          base minerals will need to be cultivated and incentivized in order for
          the new measures to have a positive impact in the mining industry in
          Zimbabwe. At the same time it will be important to ensure that the
          nation’s capacity to meet global demand for its raw minerals is not
          unduly limited in the short to medium term while the local market
          grows.
        </p>
        <p>
          That being said, it remains to be seen whether this new measure will
          be advantageous for the mining sector of Zimbabwe, especially for
          further down the value chain of mineral processing.
        </p>
        <p>
          <b>
          Coghlan Welsh & Guest is a full service law firm with an
          outstanding energy, mining and commodities practice, representing
          lenders, developers and sponsors in significant projects in Zimbabwe.
          For enquiries, please do not hesitate to contact us.</b>
        </p>
        <p className="italic">
          <b>DISCLAIMER : </b>This article is for information purposes only and does not constitute legal advice. This article is not intended to create a legal practitioner – client relationship. You should not take action based on this information without consulting one of our legal practitioners.
        </p>
      </article>
    </div>
  );
}

export default TheLaw;
